import { ChangeDetectionStrategy, Component, inject, signal, computed } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { CollectionLayoutComponent, CollectionLayoutConfig, CollectionFilterGroup, CollectionFilterValues, ZagIcon } from '@zag/ui';
import { NotificationsService } from '../../services/notifications.service';
import { NotificationItemComponent } from '../notification-item/notification-item.component';
import { type NotificationResponse } from '@zag/notifications/common';

type ReadFilter = 'all' | 'unread' | 'read';

const FILTER_GROUPS: CollectionFilterGroup<ReadFilter>[] = [
  {
    id: 'read-state',
    label: 'notifications.filterLabel',
    options: [
      { value: 'all', label: 'notifications.filters.all', icon: 'notifications' },
      { value: 'unread', label: 'notifications.filters.unread', icon: 'mark_email_unread' },
      { value: 'read', label: 'notifications.filters.read', icon: 'done_all' },
    ],
  },
];

const COLLECTION_CONFIG: CollectionLayoutConfig<NotificationResponse> = {
  title: 'notifications.title',
  icon: 'notifications',
  description: 'notifications.description',
  filterGroups: FILTER_GROUPS,
  storageKeyPrefix: 'notifications',
  initialBatchSize: 50,
  loadMoreBatchSize: 20,
  viewModes: [
    { id: 'list', label: 'common.viewList', icon: 'view-list', isCardMode: false },
    { id: 'card', label: 'common.viewCard', icon: 'grid-view', isCardMode: true },
  ],
  defaultViewMode: 'list',
};

/**
 * Full-page notification list using the standard catalog layout.
 * Route: /notifications
 */
@Component({
  selector: 'zag-notification-list',
  imports: [TranslatePipe, ZagIcon, RouterModule, NotificationItemComponent, CollectionLayoutComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './notification-list.component.html',
  styleUrls: ['./notification-list.component.scss'],
})
export class NotificationListComponent {
  private readonly notificationsService = inject(NotificationsService);
  private readonly router = inject(Router);

  readonly isMarkingAll = signal(false);
  readonly listResource = this.notificationsService.listResource;
  readonly isLoading = computed(() => this.listResource.isLoading());
  readonly error = computed(() => (this.listResource.status() === 'error' ? this.listResource.error() : null));
  readonly notifications = computed(() => this.listResource.value()?.data ?? []);
  readonly total = computed(() => this.listResource.value()?.meta.total ?? 0);
  readonly hasUnread = this.notificationsService.hasUnread;

  /** Current grouped filter state — two-way bound to the collection layout */
  readonly filterValues = signal<CollectionFilterValues>({ 'read-state': ['all'] });

  readonly collectionConfig = COLLECTION_CONFIG;
  readonly trackById = (n: NotificationResponse) => n.id;

  /**
   * Handler for the collection-layout filter change. Updates the local
   * signal that drives the template and propagates the choice to the
   * notifications service as a single event, replacing a prior `effect`
   * that coupled the two signals and required `untracked`.
   */
  onFilterChange(filterValues: CollectionFilterValues): void {
    this.filterValues.set(filterValues);
    const value = (filterValues['read-state']?.[0] as ReadFilter | undefined) ?? 'all';
    const current = this.notificationsService.listFilters();
    let read: boolean | undefined;
    if (value === 'unread') {
      read = false;
    } else if (value === 'read') {
      read = true;
    } else {
      read = undefined;
    }
    this.notificationsService.listFilters.set({ ...current, read, skip: 0 });
  }

  onMarkAsRead(id: string): void {
    this.notificationsService.markAsRead(id).subscribe(() => {
      this.listResource.reload();
    });
  }

  onArchive(id: string): void {
    this.notificationsService.archive(id).subscribe(() => {
      this.listResource.reload();
    });
  }

  onMarkAllAsRead(): void {
    this.isMarkingAll.set(true);
    this.notificationsService.markAllAsRead().subscribe(() => {
      this.isMarkingAll.set(false);
      this.listResource.reload();
    });
  }

  onRetry(): void {
    this.listResource.reload();
  }

  onNotificationClick(notification: NotificationResponse, event: Event): void {
    event.preventDefault();
    this.router.navigate(['/notifications', notification.id]);
  }
}
